import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { CrudType } from '../../../utility/types/CrudType';
import { CrudStatus } from '../../../utility/types/CrudStatus';
import RaaAuthenticatedRemoteService from '../../../utility/RaaAuthenticatedRemoteService';
import envConfig from '../../../envConfig';

const selectNotificationGroupsCrudStatus = (state) => state.notificationGroups.crudStatus;
const selectNotificationGroupsCrudOperation = (state) => state.notificationGroups.crudOperation;

const selectNotificationGroupList = createSelector([
  (state) => state.notificationGroups.notificationGroupList],
(notificationGroupList) => notificationGroupList);

// eslint-disable-next-line max-len
const selectAlertNameListForSelectedNotificationGroup = (state) => state.notificationGroups.selectedNotificationGroupAlertNameList;

const initialState = {
  notificationGroupList: [],
  selectedNotificationGroupAlertNameList: [],
  crudOperation: CrudType.READ,
  crudStatus: CrudStatus.IDLE,
  error: false,
};

const fetchNotificationGroups = createAsyncThunk(
  'securedApi/notificationGroups/fetchNotificationGroups',
  async (_, thunkAPI) => {
    try {
      return await RaaAuthenticatedRemoteService.globalInstance()
        .get(`${envConfig.apiUrl}/api/v1/notification-group/`);
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

const createNotificationGroup = createAsyncThunk(
  'securedApi/notificationGroups/createNotificationGroups',
  async (notificationGroup, thunkAPI) => {
    try {
      return await RaaAuthenticatedRemoteService.globalInstance()
        .post(`${envConfig.apiUrl}/api/v1/notification-group/`,
          { data: { ...notificationGroup } });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

const editNotificationGroup = createAsyncThunk(
  'securedApi/notificationGroups/editNotificationGroup',
  async (editNotificationGroupData, thunkAPI) => {
    const { notificationGroup, patchFields } = editNotificationGroupData;
    try {
      await RaaAuthenticatedRemoteService.globalInstance()
        .patch(`${envConfig.apiUrl}/api/v1/notification-group/${notificationGroup.id}`,
          { data: { ...notificationGroup, patchFields: [...patchFields] } });
      return thunkAPI.fulfillWithValue({ ...notificationGroup });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

const deleteNotificationGroup = createAsyncThunk(
  'securedApi/NotificationGroups/deleteNotificationGroup',
  async (id, thunkAPI) => {
    try {
      await RaaAuthenticatedRemoteService.globalInstance()
        .delete(`${envConfig.apiUrl}/api/v1/notification-group/${id}`);
      return thunkAPI.fulfillWithValue(id);
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

const fetchNotificationGroupAlertDetails = createAsyncThunk(
  'securedApi/NotificationGroups/fetchNotificationGroupAlertDetails',
  async (id, thunkAPI) => {
    try {
      return await RaaAuthenticatedRemoteService.globalInstance()
        .get(`${envConfig.apiUrl}/api/v1/alerts/notification-groups/${id}`);
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

const notificationGroupsSlice = createSlice({
  name: 'notificationGroups',
  initialState,
  reducers: {
    clearNotificationGroupsSlice: () => initialState,
    setNotificationGroupsCrudOperation: (state, action) => {
      state.crudOperation = action.payload;
    },
    removeNotificationGroup: (state, action) => {
      state.notificationGroupList = [...state.notificationGroupList]
        .filter((search) => search.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationGroups.pending, (state) => {
        state.loading = true;
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(fetchNotificationGroups.fulfilled, (state, action) => {
        const { notificationGroups } = action.payload;
        state.notificationGroupList = notificationGroups;
        state.crudStatus = CrudStatus.SUCCESS;
        state.loading = false;
        state.error = false;
      })
      .addCase(fetchNotificationGroups.rejected, (state) => {
        state.error = true;
        state.loading = false;
        state.crudStatus = CrudStatus.ERROR;
      })
      .addCase(createNotificationGroup.pending, (state) => {
        state.crudOperation = CrudType.CREATE;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(createNotificationGroup.fulfilled, (state, action) => {
        const { notificationGroup } = action.payload;
        const foundIndex = state.notificationGroupList
          .findIndex((range) => range.id === notificationGroup.id);
        if (foundIndex !== -1) {
          state.notificationGroupList.splice(foundIndex, 1, notificationGroup);
        } else {
          state.notificationGroupList.push(notificationGroup);
        }
        state.crudOperation = CrudType.CREATE;
        state.crudStatus = CrudStatus.SUCCESS;
        state.error = false;
      })
      .addCase(createNotificationGroup.rejected, (state) => {
        state.crudOperation = CrudType.CREATE;
        state.crudStatus = CrudStatus.ERROR;
        state.error = true;
      })
      .addCase(editNotificationGroup.pending, (state) => {
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(editNotificationGroup.fulfilled, (state, action) => {
        const newNotificationGroup = action.payload;
        const foundIndex = state.notificationGroupList
          .findIndex((range) => range.id === newNotificationGroup.id);
        if (foundIndex !== -1) {
          state.notificationGroupList.splice(foundIndex, 1, newNotificationGroup);
        } else {
          state.notificationGroupList.push(newNotificationGroup);
        }
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.SUCCESS;
        state.error = false;
      })
      .addCase(editNotificationGroup.rejected, (state) => {
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.ERROR;
        state.error = true;
      })
      .addCase(deleteNotificationGroup.pending, (state) => {
        state.crudOperation = CrudType.DELETE;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(deleteNotificationGroup.fulfilled, (state, action) => {
        state.notificationGroupList = [...state.notificationGroupList]
          .filter((notificationGroup) => notificationGroup.id !== action.payload);
        state.crudOperation = CrudType.DELETE;
        state.crudStatus = CrudStatus.SUCCESS;
        state.error = false;
      })
      .addCase(deleteNotificationGroup.rejected, (state) => {
        state.crudOperation = CrudType.DELETE;
        state.crudStatus = CrudStatus.ERROR;
        state.error = true;
      })
      .addCase(fetchNotificationGroupAlertDetails.pending, (state) => {
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(fetchNotificationGroupAlertDetails.fulfilled, (state, action) => {
        state.selectedNotificationGroupAlertNameList = action.payload.alertNameList;
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.SUCCESS;
        state.error = false;
      })
      .addCase(fetchNotificationGroupAlertDetails.rejected, (state) => {
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.ERROR;
        state.error = true;
      });
  },
});

const { actions, reducer } = notificationGroupsSlice;

export const {
  clearNotificationGroupsSlice,
  removeNotificationGroup,
  setNotificationGroupsCrudOperation,
} = actions;
export {
  selectNotificationGroupsCrudStatus,
  selectNotificationGroupsCrudOperation,
  selectNotificationGroupList,
  fetchNotificationGroups,
  deleteNotificationGroup,
  createNotificationGroup,
  fetchNotificationGroupAlertDetails,
  selectAlertNameListForSelectedNotificationGroup,
  editNotificationGroup,
};
export default reducer;
